import { useAtom } from "jotai";
import React, { useState } from "react";
import { currentSpaceAtom } from "../jotai/atoms";
import { useAuth } from "../auth/useAuth";
import Dialog, { Actions, Close } from "../components/Dialog";
import { Form, Input, Submit } from "../components/Form";
import { useSpaceInfo } from "../liveblocks/maps";
import { styled } from "../stitches.config";
import { ButtonForwardRef } from "../components/Button";
import { Helmet } from "react-helmet-async";
import { spaceDisplayName } from "../utils/spaceDisplayName";

// Screen-reader-friendly title
export default function SpaceName() {
  const [currentSpace] = useAtom(currentSpaceAtom);
  const [info] = useSpaceInfo();

  if (!currentSpace) return null;

  const displayName = spaceDisplayName(currentSpace?.id, info?.name);
  const title = "Sprout" + (currentSpace && ` — ${displayName}`);

  return (
    <>
      <h1 className="visually-hidden">{title}</h1>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <SpaceNameContents displayName={displayName} />
    </>
  );
}

function SpaceNameContents({ displayName }: { displayName: string }) {
  const [currentSpace] = useAtom(currentSpaceAtom);
  const [, setName] = useSpaceName();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user) return <NameSpan>{displayName}</NameSpan>;

  function changeName(newName: string) {
    if (!currentSpace) return null;

    setName(newName);
    setOpen(false);

    fetch("/api/space/update", {
      method: "POST",
      body: JSON.stringify({
        id: currentSpace.id,
        name: newName,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    }).catch((e) => console.error(e));
  }

  return (
    <Dialog
      open={open}
      setOpen={setOpen}
      label={displayName}
      title="Space name"
      ariaLabel="Edit space name"
      triggerAs={NameButton}
    >
      <NameForm currentName={displayName} changeName={changeName} />
    </Dialog>
  );
}

interface NameFormProps {
  currentName: string;
  changeName: (name: string) => void;
}

function NameForm({ currentName, changeName }: NameFormProps) {
  const [name, setName] = useState(currentName);

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setName(e.target.value);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    changeName(name);
  }

  return (
    <Form onSubmit={onSubmit}>
      <Input
        name="name"
        type="text"
        placeholder="Enter name..."
        spellCheck={false}
        required
        onChange={onChange}
        value={name}
      />
      <Actions>
        <Submit>Submit</Submit>
        <Close secondary>Cancel</Close>
      </Actions>
    </Form>
  );
}

function useSpaceName() {
  const [info, infoClient] = useSpaceInfo();
  const name = info?.name;

  function setName(newName: string) {
    infoClient?.set("name", newName);
  }
  return [name, setName] as const;
}

const nameStyles = {
  fontWeight: "$medium",
};

const NameSpan = styled("span", { ...nameStyles });
const NameButton = styled(ButtonForwardRef, { ...nameStyles });
